// @flow

import React, { type Node, Suspense } from 'react';
import { useSendPendingEvent } from '@tectonics/analytics';
import { BoletoPageContent } from './boleto';
import { CashPageContent } from './cash';
import { SpeiPageContent } from './spei';
import { RedirectPageContent } from './redirect';
import { OxxoPageContent } from './oxxo';
import { CreditCardPageContent } from './creditcard';
import { NequiPageContent } from './nequi';
import { MpesaPageContent } from './mpesa';
import { UssdPageContent } from './ussd';
import { getPaymentType, getPaymentTypeCode, useIncomingPayment } from '@tectonics/prs/incoming-payment';

const PixPage = React.lazy(() => import('./pix'));

export function PageContent(): Node {
  const incomingPayment = useIncomingPayment();
  const paymentType = getPaymentType(incomingPayment) || 'null';
  const paymentTypeCode = getPaymentTypeCode(incomingPayment) || 'null';

  useSendPendingEvent(true);

  switch (paymentType) {
    case 'boleto':
      return <BoletoPageContent />;

    case 'banktransfer':
    case 'eft':
    case 'servipag':
    case 'multicaja':
    case 'webpay':
    case 'safetypay-cash':
    case 'safetypay-online':
    case 'sencillito':
    case 'externalpayprovider':
      return <RedirectPageContent />;

    case 'cash':
      return <CashPageContent />;

    case 'spei':
      return <SpeiPageContent />;

    case 'oxxo':
      return <OxxoPageContent />;

    case 'creditcard':
      return <CreditCardPageContent />;

    default:
      return <LazyLoaded paymentType={paymentType} paymentTypeCode={paymentTypeCode} />;
  }
}

function LazyLoaded(props: { paymentType: string, paymentTypeCode: string }): Node {
  return (
    <Suspense fallback={<LoadingContent />}>
      <PageContentImpl {...props} />
    </Suspense>
  );
}

function LoadingContent(): Node {
  return null;
}

function PageContentImpl(props: { paymentType: string, paymentTypeCode: string }): Node {
  switch(props.paymentType) {
    case 'pix':
      return <PixPage />;
    case 'ewallet':
      return getEwalletPage(props.paymentTypeCode);

    default:
      throw new Error(`Pending Page: payment type: ${props.paymentType} is not implemented`);
  }
}

function getEwalletPage(paymentTypeCode: string): Node {
  switch (paymentTypeCode) {
    case 'nequi':
      return <NequiPageContent />;

    case 'mercadopago':
      return <RedirectPageContent />;

    case 'mpesa':
      return <MpesaPageContent />;

    case 'ussd':
      return <UssdPageContent />;

    default:
      throw new Error(`Pending Page: payment type code: ${paymentTypeCode} is not implemented`);
  }
}
